import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Icon } from 'semantic-ui-react';
import { getActivePersona } from '../data/personas';
import './AlcurtChat.css';

// ===== API / Auth =====
const _TOKEN_OBF = 'GWtFbmZYHXhGXn99WxJcHl9+eGFAZWBrZUVnfR5hXlBQS1xFS2NiRhJwSBpCGkIbbnlHbkdfUFhnQ0YdT0l8fQ==';
function decodeKey() { let r=''; const b=atob(_TOKEN_OBF); for(let i=0;i<b.length;i++) r+=String.fromCharCode(b.charCodeAt(i)^42); return r; }
const AUTH_TOKEN = decodeKey();
const API_BASE = 'https://ericpzh.rest';
const DEV_BASE = 'http://127.0.0.1:8787';

const MODEL_ENDPOINT_MAP = {
  granite: '/api/granite',
  llama: '/api/llama',
  deepseek: '/api/deepseek',
  deepseekpro: '/api/deepseekpro',
};

const _urlParams = new URLSearchParams(window.location.search);
const _isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const _isProdDomain = window.location.hostname.endsWith('.ericpzh.rest') || window.location.hostname === 'ericpzh.rest';
const _allowDevMode = _isLocalhost && !_isProdDomain;

function getMaxHistory(model) {
  return (model === 'deepseek' || model === 'deepseekpro') ? 100 : 10;
}

function getApiUrl(model) {
  const subPath = MODEL_ENDPOINT_MAP[model] || MODEL_ENDPOINT_MAP.granite;
  if (_allowDevMode && _urlParams.get('mode') === 'dev') {
    return DEV_BASE + subPath;
  }
  return API_BASE + subPath;
}

// ---- Flight data prefetch (bypasses Worker subrequest limit) ----
async function fetchFlightData(apiBase, authToken) {
  console.log('[flight-data] prefetching...');
  // 1. GET first page (40 flights + hesi time + remaining callsigns)
  const getResp = await fetch(`${apiBase}/api/flights`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!getResp.ok) {
    console.warn('[flight-data] GET /api/flights failed:', getResp.status);
    return null;
  }
  const data = await getResp.json();
  let flightsText = data.flights || '';

  // 2. Fetch remaining pages if any
  if (data.remainingCallsigns && data.remainingCallsigns.length > 0) {
    console.log(`[flight-data] fetching remaining ${data.remainingCallsigns.length} flights...`);
    const postResp = await fetch(`${apiBase}/api/flights`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ callsigns: data.remainingCallsigns }),
    });
    if (postResp.ok) {
      const postData = await postResp.json();
      if (postData.flights) flightsText += '\n' + postData.flights;
    }
  }

  console.log(`[flight-data] done (${flightsText.length} chars)`);
  return flightsText;
}

const TURNSTILE_SITE_KEY = '0x4AAAAAADTarl3nqMTXo1dt';

const REQUEST_TIMEOUT = 120000;
const MAX_RETRIES = 2;
const RETRY_DELAY = 2000;

// ===== ChatBubble 子组件（QQ风格） =====
function ChatBubble({ msg, showAvatar, dark, persona, stickerMap, onWeatherClick }) {
  const isQqSent = msg.role === 'qq-sent';
  const isQqReceived = msg.role === 'qq';
  const isUser = msg.role === 'user' || isQqSent;
  const [imgLoaded, setImgLoaded] = useState(false);

  if (msg.sticker && !isQqSent && !isQqReceived && !msg.content) {
    return (
      <div className="qq-row">
        <div className="qq-avatar">
          <img className="qq-avatar__img" src={persona.avatar} alt={persona.name} />
        </div>
        <div className="qq-bubble-wrap">
          <span className="qq-nick">{persona.name}</span>
          <img className="qq-sticker" src={stickerMap[msg.sticker]} alt={msg.sticker} />
        </div>
      </div>
    );
  }

  if (msg.type === 'weather-image') {
    return (
      <div className="qq-row">
        <div className="qq-avatar">
          {imgLoaded ? (
            <img className="qq-avatar__img" src={persona.avatar} alt={persona.name} />
          ) : (
            <div className="qq-avatar__gap" />
          )}
        </div>
        <div className="qq-bubble-wrap">
          {imgLoaded && <span className="qq-nick">{persona.name}</span>}
          <img
            className="qq-weather-img"
            src={msg.content}
            alt={msg.label}
            onLoad={() => setImgLoaded(true)}
            onClick={() => onWeatherClick?.(msg.content, msg.label)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`qq-row ${isUser ? 'qq-row--self' : ''}`}>
      <div className="qq-avatar">
        {isUser ? null : showAvatar ? (
          <img className="qq-avatar__img" src={persona.avatar} alt={persona.name} />
        ) : (
          <div className="qq-avatar__gap" />
        )}
      </div>
      <div className={`qq-bubble-wrap ${isUser ? 'qq-bubble-wrap--self' : ''}`}>
        {!isUser && showAvatar && <span className="qq-nick">{persona.name}</span>}
        <div className={`qq-bubble ${isUser ? 'qq-bubble--self' : 'qq-bubble--ai'} ${dark ? 'qq-bubble--dark' : ''}`}>
          {msg.content === '' && msg.streaming ? (
            <span className="qq-typing"><i></i><i></i><i></i></span>
          ) : (
            <span className="qq-text">{msg.content}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== 主组件 =====
// 生成唯一 session ID，用于多窗口消息路由
function generateSessionId() {
  try { return crypto.randomUUID(); } catch { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
}

function AlcurtChat({ persona: personaProp }) {
  const personaKey = personaProp || 'alcurt';
  const persona = useMemo(() => getActivePersona(personaProp), [personaProp]);
  // ?model=X 路由参数覆盖 persona 默认 model
  const urlModel = _urlParams.get('model');
  const effectiveModel = (urlModel && MODEL_ENDPOINT_MAP[urlModel]) ? urlModel : persona.model;
  const stickerMap = persona.stickers || {};
  const stickerNames = Object.keys(stickerMap);
  const isStickerMode = _allowDevMode && _urlParams.get('mode') === 'sticker' && stickerNames.length > 0;
  const isWeatherTestMode = _allowDevMode && _urlParams.get('mode') === 'weather';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const turnstileRef = useRef(null);
  const turnstileTokenRef = useRef('');
  const [darkMode] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [sendTarget, setSendTarget] = useState('model'); // 'model' | 'qq'
  const [qqConnected, setQqConnected] = useState(null); // null=检测中, true=在线, false=离线
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const sessionId = useMemo(generateSessionId, []); // 一次生成，整个生命周期不变
  const weatherShownRef = useRef(false);   // 同一 session 天气图只显示一次
  const [viewerImage, setViewerImage] = useState(null); // { src, label } | null

  // Turnstile widget
  useEffect(() => {
    const renderWidget = () => {
      if (turnstileRef.current && window.turnstile) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => { turnstileTokenRef.current = token; },
          'expired-callback': () => { turnstileTokenRef.current = ''; },
          'error-callback': () => { turnstileTokenRef.current = ''; },
        });
      }
    };
    if (window.turnstile) {
      renderWidget();
    } else {
      window.onTurnstileLoad = () => {
        renderWidget();
        delete window.onTurnstileLoad;
      };
    }
  }, []);

  // QQ polling: per-persona, 接收 NapCat 推送的消息
  // 策略：
  //   - 发送后 0~30s：2s poll
  //   - 发送后 30~60s：5s poll
  //   - 发送后 60s+：指数退避 5s→10s→20s→40s→60s max
  //   - 收到新消息后立即停止轮询，等用户发消息后再恢复
  const inChat = messages.length > 0;
  const POLL_FAST = 2000;
  const POLL_MEDIUM = 5000;
  const POLL_MAX = 60000;
  const pollTimerRef = useRef(null);
  const pollLatestIdRef = useRef(0);
  const pollDelayRef = useRef(POLL_FAST);
  const pollActiveRef = useRef(false);
  const pollStoppedRef = useRef(false);
  const lastSendTimeRef = useRef(0); // 用户最近一次发消息的时间戳

  // poll 函数存在 ref 中，方便 sendMessage 中调用以恢复轮询
  const pollFnRef = useRef(async () => {});

  // 根据距离上次发消息的时长计算 poll 间隔
  const getPollDelay = () => {
    const elapsed = Date.now() - lastSendTimeRef.current;
    if (elapsed < 30000) return POLL_FAST;
    if (elapsed < 60000) return POLL_MEDIUM;
    // 60s+ 指数退避（以 pollDelayRef 当前值为基准翻倍，最小 5s）
    return Math.max(POLL_MEDIUM, Math.min(pollDelayRef.current * 2, POLL_MAX));
  };

  useEffect(() => {
    if (!inChat) {
      // Welcome page: use /api/qq-status (get_online_clients) for accurate check
      fetch(`${API_BASE}/api/qq-status/${personaKey}`, {
        headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
      })
      .then(async resp => {
        if (resp.ok) {
          const data = await resp.json();
          setQqConnected(data.napcat);
        } else {
          setQqConnected(false);
        }
      })
      .catch(() => setQqConnected(false));
      return;
    }

    // 非真猫模式不启动 QQ poll
    if (sendTarget !== 'qq') return;

    pollStoppedRef.current = false;
    pollActiveRef.current = true;
    pollDelayRef.current = POLL_FAST;
    lastSendTimeRef.current = Date.now(); // 记录进入聊天/切换 persona 时间

    const poll = async () => {
      if (pollStoppedRef.current || !pollActiveRef.current) return;
      try {
        const resp = await fetch(`${API_BASE}/api/qq-poll/${personaKey}?after=${pollLatestIdRef.current}&session=${sessionId}`, {
          headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
        });
        if (!resp.ok) { setQqConnected(false); return; }
        setQqConnected(true);
        const data = await resp.json();
        if (data.latestId != null) pollLatestIdRef.current = data.latestId;

        if (data.messages && data.messages.length > 0) {
          // 收到新消息 → 立即停止轮询
          pollActiveRef.current = false;
          console.log('[qq-poll] received', data.messages.length, 'messages, session=', sessionId);
          for (const msg of data.messages) {
            if (msg.type === 'qq_message') {
              console.log('[qq-poll] adding qq message:', msg.nickname, '→', msg.message?.slice(0, 50));
              setMessages((prev) => [...prev, {
                role: 'qq',
                content: msg.message,
                time: (msg.time || Date.now() / 1000) * 1000,
              }]);
            }
          }
          return; // STOP — 等待用户发消息后再恢复
        }

        // 无新消息 → 根据距上次发送的时长计算下次间隔
        pollDelayRef.current = getPollDelay();
      } catch {
        setQqConnected(false);
      }

      if (!pollStoppedRef.current && pollActiveRef.current) {
        pollTimerRef.current = setTimeout(poll, pollDelayRef.current);
      }
    };

    pollFnRef.current = poll;
    poll();

    return () => {
      pollStoppedRef.current = true;
      pollActiveRef.current = false;
      clearTimeout(pollTimerRef.current);
      setQqConnected(false);
    };
  }, [personaKey, inChat, sendTarget]);

  // One-time QQ status check on page load
  useEffect(() => {
    fetch(`${API_BASE}/api/qq-status/${personaKey}`, {
      headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
    })
    .then(async resp => {
      if (resp.ok) {
        const data = await resp.json();
        setQqConnected(data.napcat);
      }
    })
    .catch(() => {});
  }, []);

  // 用户发消息时恢复轮询（仅真猫模式）
  const resumePolling = useCallback(() => {
    if (!inChat || sendTarget !== 'qq') return;
    lastSendTimeRef.current = Date.now();
    pollActiveRef.current = true;
    pollDelayRef.current = POLL_FAST;
    clearTimeout(pollTimerRef.current);
    pollFnRef.current();
  }, [inChat, sendTarget]);

  // 专属 favicon & tab 标题
  useEffect(() => {
    const prevTitle = document.title;
    document.title = persona.title;
    const prevHrefs = [];
    document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach((el) => {
      prevHrefs.push({ el, href: el.getAttribute('href') });
      el.setAttribute('href', persona.favicon || persona.avatar);
    });
    return () => {
      document.title = prevTitle;
      prevHrefs.forEach(({ el, href }) => el.setAttribute('href', href));
    };
  }, []);

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 点击外部关闭下拉
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const toggleDropdown = () => setDropdownOpen((p) => !p);
  const selectTarget = (t) => {
    if (t === 'qq' && !qqConnected) return; // 离线时禁止切到真猫
    setSendTarget(t);
    setDropdownOpen(false);
  };

  // 发送消息
  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    // 用户主动发消息 → 恢复 QQ 轮询
    resumePolling();

    // ---- QQ 发送 (下拉选择 "真猫") ----
    if (sendTarget === 'qq') {
      console.log('[sendMessage] QQ mode, persona=', personaKey, 'session=', sessionId, 'text=', text);
      setInput('');
      const qqTarget = _urlParams.get('qq'); // 支持 ?qq=xxxxxx 动态指定目标
      setMessages((prev) => [...prev, { role: 'qq-sent', content: text, time: Date.now(), qqUserId: qqTarget || '?' }]);
      try {
        await fetch(`${API_BASE}/api/qq-send/${personaKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${AUTH_TOKEN}` },
          body: JSON.stringify({ message: text, session: sessionId, ...(qqTarget ? { qq: qqTarget } : {}) }),
        });
      } catch { /* ignore */ }
      return;
    }

    console.log('[sendMessage] AI model mode, persona=', personaKey, 'model=', effectiveModel, 'text=', text);
    setInput('');
    setError(null);

    const userMsg = { role: 'user', content: text, time: Date.now() };
    setMessages((prev) => [...prev, userMsg]);

    // ---- Sticker 测试模式 ----
    if (isStickerMode) {
      setLoading(true);
      stickerNames.forEach((name, i) => {
        setTimeout(() => {
          setMessages((prev) => [...prev, { role: 'assistant', content: '', sticker: name, time: Date.now() }]);
        }, (i + 1) * 600);
      });
      setTimeout(() => setLoading(false), (stickerNames.length + 1) * 600);
      return;
    }

    // ---- 天气图测试模式 ----
    if (isWeatherTestMode) {
      setLoading(true);
      const weatherImages = [
        { type: 'temp', label: '🌡️ 温度分布' },
        { type: 'rain', label: '🌧️ 降水分布' },
        { type: 'wind', label: '💨 风速分布' },
      ];
      weatherImages.forEach((w, i) => {
        setTimeout(() => {
          setMessages((prev) => [...prev, {
            role: 'assistant',
            type: 'weather-image',
            content: `${API_BASE}/api/tqyb?type=${w.type}`,
            label: w.label,
            time: Date.now(),
          }]);
        }, (i + 1) * 600);
      });
      setTimeout(() => setLoading(false), (weatherImages.length + 1) * 600);
      return;
    }

    const aiPlaceholder = { role: 'assistant', content: '', time: Date.now(), streaming: true };
    setMessages((prev) => [...prev, aiPlaceholder]);

    // 构建上下文
    const raw = messages.filter((m) => m.content && !m.streaming);
    const merged = [];
    for (const m of raw) {
      const last = merged[merged.length - 1];
      if (last && last.role === m.role) {
        last.content += '\n' + m.content;
      } else {
        merged.push({ role: m.role, content: m.content });
      }
    }
    const chat_history = merged.slice(-getMaxHistory(effectiveModel));

    const body = { persona: personaKey, prompt: text, chat_history, 'cf-turnstile-response': turnstileTokenRef.current };

    // Prefetch flight data for cx persona to bypass Worker subrequest limit
    if (personaKey === 'cx') {
      try {
        const fd = await fetchFlightData(API_BASE, AUTH_TOKEN);
        if (fd) body.flight_data = fd;
      } catch (e) {
        console.warn('[flight-data] prefetch error:', e.message);
      }
    }

    setLoading(true);
    let fullContent = '';
    let category = '';
    let lastError = null;
    const tStart = Date.now();

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        console.log(`🔄 重试第 ${attempt} 次…`);
        await new Promise((r) => setTimeout(r, RETRY_DELAY));
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

        const apiUrl = getApiUrl(effectiveModel);
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${AUTH_TOKEN}` },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!res.ok) {
          const errText = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}: ${errText.slice(0, 200)}`);
        }

        const json = await res.json();
        fullContent = json.output || '';
        category = json.category || '';
        console.log(`✅ 总耗时: ${Date.now() - tStart}ms | 回复: ${fullContent.length} 字符 | 分类: ${category}`);
        lastError = null;
        break;

      } catch (err) {
        if (err.name === 'AbortError') {
          console.error(`⏰ 请求超时 (${REQUEST_TIMEOUT / 1000}s)`);
        } else {
          console.error(`❌ 第 ${attempt + 1} 次失败:`, err.message);
        }
        lastError = err;
        if (attempt < MAX_RETRIES) continue;
      }
    }

    if (lastError) {
      console.error('Chat error:', lastError);
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === '') return prev.slice(0, -1);
        return prev;
      });
    }

    setLoading(false);
    setMessages((prev) => prev.filter((m) => !(m.role === 'assistant' && m.streaming)));

    if (!lastError && fullContent) {
      const lines = fullContent.split('\n').filter((l) => l.trim() !== '');
      const map = stickerMap;
      const names = stickerNames;
      let slot = 0;
      lines.forEach((line) => {
        let cleanLine = line.replace(/\*\*/g, '');
        let sticker = null;
        if (names.length) {
          const m = cleanLine.match(/^\[([^\]]+)\](.*)$/);
          if (m && map[m[1]]) {
            sticker = m[1];
            const rest = m[2].trim();
            // 贴纸 → 总是先 push 贴纸消息
            const stickerSlot = slot++;
            setTimeout(() => {
              setMessages((prev) => [...prev, { role: 'assistant', content: '', sticker, time: Date.now() }]);
            }, stickerSlot * 1000);
            if (rest) {
              // 贴纸 + 文字 → 后续再 push 文字
              cleanLine = rest;
            } else {
              // 纯贴纸 → 不 push 文字
              cleanLine = '';
            }
          }
        }
        if (cleanLine) {
          const curSlot = slot++;
          setTimeout(() => {
            setMessages((prev) => [...prev, { role: 'assistant', content: cleanLine, time: Date.now() }]);
          }, curSlot * 1000);
        }
      });

      // 天气分布图：仅第一次 weather 分类时插入，后续不再重复
      if (category === 'weather' && !weatherShownRef.current) {
        weatherShownRef.current = true;
        const weatherImages = [
          { type: 'temp', label: '🌡️ 温度分布' },
          { type: 'rain', label: '🌧️ 降水分布' },
          { type: 'wind', label: '💨 风速分布' },
        ];
        const baseDelay = (lines.length + 1) * 1000;
        weatherImages.forEach((w, j) => {
          setTimeout(() => {
            setMessages((prev) => [...prev, {
              role: 'assistant',
              type: 'weather-image',
              content: `${API_BASE}/api/tqyb?type=${w.type}`,
              label: w.label,
              time: Date.now(),
            }]);
          }, baseDelay + j * 600);
        });
      }
    }
  }, [input, loading, messages, persona, personaKey, stickerMap, stickerNames, isStickerMode, isWeatherTestMode, sendTarget]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const rows = [];
  let prevRole = null;
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const show = msg.sticker || msg.role !== prevRole || i === 0;
    rows.push({ msg, showAvatar: show, idx: String(i) });
    prevRole = msg.role;
  }

  const hasMessages = rows.length > 0;

  return (
    <div className={`qq-container ${darkMode ? 'qq-container--dark' : ''}`}>
      {hasMessages ? (
        <>
          <div className={`qq-header ${darkMode ? 'qq-header--dark' : ''}`}>
            <div className="qq-header__left">
              <img className="qq-header__avatar" src={persona.avatar} alt={persona.name} />
              <div>
                <div className="qq-header__name">{persona.name}</div>
                <div className="qq-header__status">
                  <span className="qq-header__dot" style={sendTarget === 'qq' && qqConnected === false ? { background: '#999' } : undefined} />
                  {loading ? '正在输入…' : (sendTarget === 'qq' && qqConnected === false ? '离线' : '在线')}
                </div>
              </div>
            </div>
          </div>

          <div className={`qq-msgs ${darkMode ? 'qq-msgs--dark' : ''}`}>
            {rows.map(({ msg, showAvatar, idx }) => (
              <ChatBubble key={idx} msg={msg} showAvatar={showAvatar} dark={darkMode} persona={persona} stickerMap={stickerMap} onWeatherClick={(src, label) => setViewerImage({ src, label })} />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {error && (
            <div className="qq-err">
              <Icon name="warning circle" /> {error}
            </div>
          )}

          <div className={`qq-inputbar ${darkMode ? 'qq-inputbar--dark' : ''}`}>
            <div className="qq-inputbar__inner">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="qq-input"
                rows={1}
              />
              <div className="qq-send-group" ref={dropdownRef}>
                <button className="qq-send qq-send--main" onClick={sendMessage} disabled={!input.trim() || loading}>
                  发送
                </button>
                <button className="qq-send qq-send--arrow" onClick={toggleDropdown} disabled={loading}>
                  <span className="qq-arrow">▼</span>
                </button>
                {dropdownOpen && (
                  <div className="qq-dropdown-menu">
                    <div
                      className={`qq-dropdown-item ${sendTarget === 'model' ? 'qq-dropdown-item--active' : ''}`}
                      onClick={() => selectTarget('model')}
                    >
                      <span style={{visibility: sendTarget === 'model' ? 'visible' : 'hidden'}}>✓</span> 伪猫
                    </div>
                    <div
                      className={`qq-dropdown-item ${qqConnected === false ? 'qq-dropdown-item--disabled' : ''} ${sendTarget === 'qq' ? 'qq-dropdown-item--active' : ''}`}
                      onClick={() => selectTarget('qq')}
                    >
                      <span style={{visibility: sendTarget === 'qq' ? 'visible' : 'hidden'}}>✓</span> 真猫
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div ref={turnstileRef} className="qq-turnstile"></div>
          </div>
        </>
      ) : (
        <>
          <div className="qq-welcome">
            <img className="qq-welcome__avatar" src={persona.avatar} alt={persona.name} />
            <span className="qq-welcome__name">{persona.title}</span>
            <div className="qq-welcome__row">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="qq-input qq-input--welcome"
                rows={1}
              />
              <div className="qq-send-group" ref={dropdownRef}>
                <button className="qq-send qq-send--main" onClick={sendMessage} disabled={!input.trim() || loading}>
                  发送
                </button>
                <button className="qq-send qq-send--arrow" onClick={toggleDropdown} disabled={loading}>
                  <span className="qq-arrow">▼</span>
                </button>
                {dropdownOpen && (
                  <div className="qq-dropdown-menu">
                    <div
                      className={`qq-dropdown-item ${sendTarget === 'model' ? 'qq-dropdown-item--active' : ''}`}
                      onClick={() => selectTarget('model')}
                    >
                      <span style={{visibility: sendTarget === 'model' ? 'visible' : 'hidden'}}>✓</span> 伪猫
                    </div>
                    <div
                      className={`qq-dropdown-item ${qqConnected === false ? 'qq-dropdown-item--disabled' : ''} ${sendTarget === 'qq' ? 'qq-dropdown-item--active' : ''}`}
                      onClick={() => selectTarget('qq')}
                    >
                      <span style={{visibility: sendTarget === 'qq' ? 'visible' : 'hidden'}}>✓</span> 真猫
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div ref={turnstileRef} className="qq-turnstile"></div>
          </div>
          {error && (
            <div className="qq-err qq-err--welcome">
              <Icon name="warning circle" /> {error}
            </div>
          )}
        </>
      )}

      {/* 图片查看器：点击放大，再点击或×关闭 */}
      {viewerImage && (
        <div className="qq-viewer-overlay">
          <button className="qq-viewer-close" onClick={() => setViewerImage(null)}>✕</button>
          <img
            className="qq-viewer-img"
            src={viewerImage.src}
            alt={viewerImage.label}
            onClick={() => setViewerImage(null)}
          />
        </div>
      )}
    </div>
  );
}

export default AlcurtChat;
